import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Download,
  MessageCircle,
  MessageSquarePlus,
  Pencil,
  Share2,
  FilePlus,
} from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { pageActionButtonClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { ViewReviewTab } from "@/components/review/ViewReviewTab";
import { EditReviewSheet } from "@/components/review/EditReviewSheet";
import { NewVersionSheet } from "@/components/review/NewVersionSheet";
import { ReviewVersionSelector } from "@/components/review/ReviewVersionSelector";
import { DiscussionsTab } from "@/components/discussions";
import { ShareReviewDialog } from "@/components/collaboration";
import { ReviewProgressLayout } from "@/components/review/ReviewProgressLayout";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { MobileSpeedDial } from "@/components/common/MobileSpeedDial";
import { useReviews } from "@/hooks/useSupabase";
import { useEditReview } from "@/hooks/useEditReview";
import { useNewVersion } from "@/hooks/useNewVersion";
import { usePermission } from "@/contexts/AuthContext";
import { applySseProgressEvent } from "@/lib/contract-review-sse";
import { getContractPublicUrl } from "@/lib/contracts";
import { formatReviewVersionLabel } from "@/lib/review-display";
import {
  mapReviewUserError,
  REVIEW_USER_ERROR,
} from "@/lib/review-user-errors";
import type { SelectedDriveFile } from "@/components/wizard/GoogleDrivePicker";
import type { ProgressState, ReviewFormData, ReviewWithOwner, SSEEventData } from "@/types";

export function ContractReviewPage() {
  const { id: reviewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as "view" | "discussions" | null;
  const discussionFromUrl = searchParams.get("discussion");

  const [activeTab, setActiveTab] = useState<"view" | "discussions">(
    tabFromUrl === "discussions" ? "discussions" : "view",
  );
  const [review, setReview] = useState<ReviewWithOwner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showNewVersionDialog, setShowNewVersionDialog] = useState(false);
  const [regenerateProgress, setRegenerateProgress] = useState<ProgressState | null>(null);
  const [newVersionProgress, setNewVersionProgress] = useState<ProgressState | null>(null);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [newVersionError, setNewVersionError] = useState<string | null>(null);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [newDiscussionOpen, setNewDiscussionOpen] = useState(false);
  const { getById } = useReviews();
  const { regenerate } = useEditReview();
  const { createNewVersion } = useNewVersion();
  const canUpdate = usePermission("review", "update");
  const canRegenerate = usePermission("review", "regenerate");
  const canCreate = usePermission("review", "create");
  const canShare = usePermission("review", "share");
  const canEditReview = review?.is_owner && (canUpdate || canRegenerate);
  const canCreateNewVersion = Boolean(review?.is_owner && canCreate);
  const canShareReview = Boolean(review?.is_owner && canShare);
  const shareReviewId = review ? (review.root_review_id ?? review.id) : "";
  const isProcessing = regenerateProgress !== null || newVersionProgress !== null;

  const refetchReview = useCallback(async () => {
    if (!reviewId) return;
    try {
      const result = await getById(reviewId);
      setReview(result);
    } catch (e) {
      console.error(e);
    }
  }, [reviewId, getById]);

  const runRegenerate = useCallback(
    async (formData: ReviewFormData) => {
      if (!reviewId) return;
      setRegenerateError(null);
      setRegenerateProgress({
        message: "Connecting...",
        progress: 0,
        isConnected: false,
        logs: [],
      });

      try {
        const result = await regenerate(reviewId, formData, {
          onProgress: (data: SSEEventData) => {
            setRegenerateProgress((prev) => (prev ? applySseProgressEvent(prev, data) : prev));
          },
          onHeartbeat: () => {
            setRegenerateProgress((prev) => (prev ? { ...prev, isConnected: true } : prev));
          },
        });

        if (result?.reviewId) {
          await refetchReview();
        } else {
          setRegenerateError(
            "Review could not be completed. Check the server logs or try again.",
          );
        }
      } catch (err) {
        setRegenerateError(
          err instanceof Error ? err.message : "Something went wrong. Please try again.",
        );
      } finally {
        setRegenerateProgress(null);
      }
    },
    [reviewId, regenerate, refetchReview],
  );

  const runNewVersion = useCallback(
    async (
      file: File | null,
      driveFile: SelectedDriveFile | null,
      formData: ReviewFormData,
    ) => {
      if (!reviewId) return;
      setShowNewVersionDialog(false);
      setNewVersionError(null);
      setNewVersionProgress({
        message: "Connecting...",
        progress: 0,
        isConnected: false,
        logs: [],
      });

      try {
        const result = await createNewVersion(reviewId, file, driveFile, formData, {
          onProgress: (data: SSEEventData) => {
            setNewVersionProgress((prev) => (prev ? applySseProgressEvent(prev, data) : prev));
          },
          onHeartbeat: () => {
            setNewVersionProgress((prev) => (prev ? { ...prev, isConnected: true } : prev));
          },
        });

        if (result?.reviewId) {
          navigate(`/contract-review/${result.reviewId}`, { replace: true });
        } else {
          setNewVersionError(REVIEW_USER_ERROR.createNewVersionFailed);
        }
      } catch (err) {
        const raw = err instanceof Error ? err.message : REVIEW_USER_ERROR.generic;
        setNewVersionError(mapReviewUserError(raw));
      } finally {
        setNewVersionProgress(null);
      }
    },
    [reviewId, createNewVersion, navigate],
  );

  const handleTabChange = (tab: "view" | "discussions") => {
    if (tab !== "view") {
      setMobileChatOpen(false);
    }
    if (tab !== "discussions") {
      setNewDiscussionOpen(false);
    }
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    if (tab === "discussions") {
      newParams.set("tab", tab);
    } else {
      newParams.delete("tab");
    }
    newParams.delete("discussion");
    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    if (!reviewId) return;

    setIsLoading(true);
    setError(null);
    void getById(reviewId)
      .then((result) => setReview(result))
      .catch((reason) => {
        const message = reason instanceof Error ? reason.message : "Failed to load review";
        setError(message);
      })
      .finally(() => setIsLoading(false));
  }, [reviewId, getById]);

  const handleDownload = useCallback(async () => {
    if (!review?.contract_file_path) return;

    const downloadUrl = await getContractPublicUrl(review.contract_file_path);
    if (!downloadUrl) return;

    const link = window.document.createElement("a");
    link.href = downloadUrl;
    link.download = review.file_name || "contract.pdf";
    link.target = "_blank";
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }, [review?.contract_file_path, review?.file_name]);

  const speedDialActions = useMemo(() => {
    if (!review) return [];

    const actions = [];

    if (canShareReview) {
      actions.push({
        id: "share",
        icon: Share2,
        label: "Share",
        onClick: () => setShowShareDialog(true),
      });
    }

    if (canCreateNewVersion) {
      actions.push({
        id: "new-version",
        icon: FilePlus,
        label: "New version",
        disabled: isProcessing,
        onClick: () => {
          setNewVersionError(null);
          setShowNewVersionDialog(true);
        },
      });
    }

    if (canEditReview) {
      actions.push({
        id: "edit",
        icon: Pencil,
        label: "Edit",
        disabled: isProcessing,
        onClick: () => {
          setRegenerateError(null);
          setShowEditSheet(true);
        },
      });
    }

    if (review.contract_file_path) {
      actions.push({
        id: "download",
        icon: Download,
        label: "Download",
        onClick: () => {
          void handleDownload();
        },
      });
    }

    if (activeTab === "view") {
      actions.push({
        id: "chat",
        icon: MessageCircle,
        label: "Chat",
        onClick: () => setMobileChatOpen(true),
      });
    }

    if (activeTab === "discussions") {
      actions.push({
        id: "new-thread",
        icon: MessageSquarePlus,
        label: "New thread",
        onClick: () => setNewDiscussionOpen(true),
      });
    }

    return actions;
  }, [
    review,
    canShareReview,
    canCreateNewVersion,
    canEditReview,
    isProcessing,
    activeTab,
    handleDownload,
  ]);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-[#f4f5f9] dark:bg-slate-950">
        <div className="text-slate-400">Loading review...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-[#f4f5f9] text-red-400 dark:bg-slate-950">
        <p>Error: {error}</p>
        <Link to="/contract-review" className="mt-4 text-violet-400 hover:text-violet-300">
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-[#f4f5f9] text-slate-400 dark:bg-slate-950">
        <p>Review not found</p>
        <Link to="/contract-review" className="mt-4 text-violet-400 hover:text-violet-300">
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (newVersionProgress || newVersionError) {
    return (
      <ReviewProgressLayout
        progress={newVersionProgress ?? undefined}
        error={newVersionError}
        onErrorDismiss={() => setNewVersionError(null)}
      />
    );
  }

  if (regenerateProgress) {
    return (
      <ReviewProgressLayout
        title="Re-analyzing contract…"
        progress={regenerateProgress}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#f4f5f9] dark:bg-slate-950">
      {regenerateError || newVersionError ? (
        <div className="shrink-0 border-b border-red-500/20 bg-red-950/40 px-6 py-3">
          <ErrorAlert message={regenerateError ?? newVersionError ?? ""} className="mt-0" />
          <button
            type="button"
            onClick={() => {
              setRegenerateError(null);
              setNewVersionError(null);
            }}
            className="mt-2 text-xs text-slate-400 underline hover:text-slate-200"
          >
            Dismiss
          </button>
        </div>
      ) : null}
      <header className="border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-4 py-4 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80 sm:px-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <IconButton asChild title="Back to contract reviews">
              <Link to="/contract-review">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </IconButton>
            <div className="flex min-w-0 items-center gap-3">
              <div className="p-2 bg-violet-500/10 rounded-lg">
                <FileText className="w-5 h-5 text-violet-400" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                  {review.file_name || "Contract Review"}
                </h1>
                <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
                  {review.contract_type || "Contract"} ·{" "}
                  {review.version ??
                    formatReviewVersionLabel(review.version_number, review.created_at)}{" "}
                  · Created{" "}
                  {new Date(review.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="hidden shrink-0 items-center gap-2 sm:flex sm:self-start">
            {canCreateNewVersion ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setNewVersionError(null);
                  setShowNewVersionDialog(true);
                }}
                disabled={isProcessing}
                title="New version"
                aria-label="New version"
                className={pageActionButtonClassName}
              >
                <FilePlus className="h-4 w-4" />
                <span className={pageActionLabelClassName}>New version</span>
              </Button>
            ) : null}
            {canShareReview ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowShareDialog(true)}
                title="Share"
                aria-label="Share"
                className={pageActionButtonClassName}
              >
                <Share2 className="h-4 w-4" />
                <span className={pageActionLabelClassName}>Share</span>
              </Button>
            ) : null}
            {canEditReview ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRegenerateError(null);
                  setShowEditSheet(true);
                }}
                disabled={isProcessing}
                title="Edit"
                aria-label="Edit"
                className={pageActionButtonClassName}
              >
                <Pencil className="h-4 w-4" />
                <span className={pageActionLabelClassName}>Edit</span>
              </Button>
            ) : null}
            {review.contract_file_path ? (
              <Button
                type="button"
                variant="accent"
                onClick={handleDownload}
                title="Download Contract"
                aria-label="Download Contract"
                className={pageActionButtonClassName}
              >
                <Download className="h-4 w-4" />
                <span className={pageActionLabelClassName}>Download</span>
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full flex-wrap gap-1 rounded-lg bg-[var(--tab-segment-bg)] p-1 sm:w-fit dark:bg-slate-800/50">
          <button
            onClick={() => handleTabChange("view")}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${
              activeTab === "view"
                ? "bg-[var(--tab-segment-active-bg)] text-[#0b1120] shadow-md dark:bg-slate-900 dark:text-white"
                : "text-[#576274] hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:text-slate-400 dark:hover:bg-slate-700/50 dark:hover:text-slate-200"
            }`}
          >
            <FileText size={16} />
            View Report
          </button>
          <button
            onClick={() => handleTabChange("discussions")}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${
              activeTab === "discussions"
                ? "bg-[var(--tab-segment-active-bg)] text-[#0b1120] shadow-md dark:bg-slate-900 dark:text-white"
                : "text-[#576274] hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:text-slate-400 dark:hover:bg-slate-700/50 dark:hover:text-slate-200"
            }`}
          >
            <MessageCircle size={16} />
            Discussions
          </button>
          </div>
          {review.versions && review.versions.length > 1 ? (
            <div className="flex shrink-0 self-end sm:self-auto">
              <ReviewVersionSelector
                currentReviewId={review.id}
                versions={review.versions}
              />
            </div>
          ) : null}
        </div>
      </header>

      <div className="flex-1 overflow-hidden">
        {activeTab === "view" && (
          <ViewReviewTab
            review={review}
            mobileChatOpen={mobileChatOpen}
            onMobileChatOpenChange={setMobileChatOpen}
            hideMobileChatFab
          />
        )}
        {activeTab === "discussions" && (
          <DiscussionsTab
            reviewId={review.id}
            review={review}
            initialDiscussionId={discussionFromUrl || undefined}
            newDiscussionOpen={newDiscussionOpen}
            onNewDiscussionOpenChange={setNewDiscussionOpen}
          />
        )}
      </div>

      {canEditReview ? (
        <EditReviewSheet
          review={review}
          isOpen={showEditSheet}
          onClose={() => setShowEditSheet(false)}
          onRequestRegenerate={runRegenerate}
        />
      ) : null}

      {canCreateNewVersion && review ? (
        <NewVersionSheet
          sourceReview={review}
          isOpen={showNewVersionDialog}
          isSubmitting={newVersionProgress !== null}
          onClose={() => setShowNewVersionDialog(false)}
          onSubmit={runNewVersion}
        />
      ) : null}

      {canShareReview && review ? (
        <ShareReviewDialog
          reviewId={shareReviewId}
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
        />
      ) : null}

      {!mobileChatOpen && !newDiscussionOpen ? <MobileSpeedDial actions={speedDialActions} /> : null}
    </div>
  );
}
